import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { api, errMsg, fmtDate, fmtDateTime } from "../lib/api";
import { useFetch } from "../lib/hooks";
import { PageTitle, Panel, Empty, Field, inputCls, btnPrimary, btnGhost, StatusBadge } from "./ui";
import { BrandLoader } from "../components/site/Logo";

export const DECISIONS = {
  not_checked: "Nije provjereno", verified: "Tačno", partially_verified: "Djelimično tačno",
  misleading: "Obmanjujuće", false: "Netačno", unverifiable: "Nije moguće provjeriti",
};
const SRC = { official: "Službeni izvor", media: "Medij", study: "Studija", statement: "Izjava", other: "Ostalo" };
const EMPTY = { text: "", decision: "not_checked", evidence: [], notes: "", recheck_at: "" };

const Evidence = ({ list, onChange, idp }) => (
  <div className="space-y-2">
    {list.map((e, i) => (
      <div key={i} className="grid gap-2 rounded border border-border p-2 md:grid-cols-[1fr_1fr_150px_auto]">
        <input placeholder="Naziv dokaza / dokumenta" value={e.title} onChange={(x) => onChange(list.map((y, j) => (j === i ? { ...y, title: x.target.value } : y)))} className={inputCls} data-testid={`${idp}-ev-title-${i}`} />
        <input placeholder="https://…" value={e.url} onChange={(x) => onChange(list.map((y, j) => (j === i ? { ...y, url: x.target.value } : y)))} className={inputCls} data-testid={`${idp}-ev-url-${i}`} />
        <select value={e.source_type} onChange={(x) => onChange(list.map((y, j) => (j === i ? { ...y, source_type: x.target.value } : y)))} className={inputCls} data-testid={`${idp}-ev-type-${i}`}>{Object.entries(SRC).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select>
        <button type="button" onClick={() => onChange(list.filter((_, j) => j !== i))} aria-label="Ukloni dokaz" className="text-destructive"><Trash2 className="h-4 w-4" /></button>
      </div>
    ))}
    <button type="button" onClick={() => onChange([...list, { title: "", url: "", source_type: "official", note: "" }])} className="text-xs font-semibold text-pulse hover:underline" data-testid={`${idp}-add-evidence`}>+ Dodaj dokaz</button>
  </div>
);

const ClaimForm = ({ initial, onSave, onDelete, idp }) => {
  const [f, setF] = useState({ ...EMPTY, ...initial, recheck_at: (initial?.recheck_at || "").slice(0, 10) });
  const submit = async (e) => {
    e.preventDefault();
    await onSave({ ...f, evidence: f.evidence.filter((x) => x.title.trim()), recheck_at: f.recheck_at ? `${f.recheck_at}T08:00:00+00:00` : "" });
    if (!initial) setF(EMPTY);
  };
  return (
    <form onSubmit={submit} className="space-y-3 rounded-lg border border-border bg-card p-4" data-testid={idp}>
      <Field label="Tvrdnja iz teksta" htmlFor={`${idp}-t`}><textarea id={`${idp}-t`} required minLength={5} rows={2} value={f.text} onChange={(e) => setF({ ...f, text: e.target.value })} className={inputCls} data-testid={`${idp}-text`} /></Field>
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="Odluka" htmlFor={`${idp}-d`}><select id={`${idp}-d`} value={f.decision} onChange={(e) => setF({ ...f, decision: e.target.value })} className={inputCls} data-testid={`${idp}-decision`}>{Object.entries(DECISIONS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        <Field label="Ponovna provjera (datum)" htmlFor={`${idp}-r`}><input id={`${idp}-r`} type="date" value={f.recheck_at} onChange={(e) => setF({ ...f, recheck_at: e.target.value })} className={inputCls} data-testid={`${idp}-recheck`} /></Field>
      </div>
      <Field label="Dokazi"><Evidence list={f.evidence} onChange={(ev) => setF({ ...f, evidence: ev })} idp={idp} /></Field>
      <Field label="Interne bilješke" htmlFor={`${idp}-n`}><textarea id={`${idp}-n`} rows={2} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} className={inputCls} /></Field>
      {initial?.reviewer_name && <p className="text-xs text-muted-foreground">Odluku donio/la: {initial.reviewer_name} · {fmtDateTime(initial.decided_at)}</p>}
      <div className="flex gap-2">
        <button className={btnPrimary} data-testid={`${idp}-save`}>{initial ? "Sačuvaj tvrdnju" : <><Plus className="h-4 w-4" /> Dodaj tvrdnju</>}</button>
        {onDelete && <button type="button" onClick={onDelete} className={btnGhost} data-testid={`${idp}-delete`}>Obriši</button>}
      </div>
    </form>
  );
};

export function FactCheckWorkspace() {
  const { id } = useParams();
  const [tick, setTick] = useState(0);
  const { data: article } = useFetch(`/admin/articles/${id}`);
  const { data: claims, loading } = useFetch(`/admin/articles/${id}/claims`, [tick]);
  const reload = () => setTick((t) => t + 1);
  const call = async (fn, msg) => {
    try { await fn(); toast.success(msg); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  if (loading || !article) return <BrandLoader />;
  return (
    <div data-testid="factcheck-workspace">
      <PageTitle title="Provjera činjenica" sub={article.headline} actions={<Link to={`/admin/articles/${id}`} className={btnGhost} data-testid="fc-open-editor">Otvori članak</Link>} />
      <p className="mb-6 text-sm text-muted-foreground">Status: <StatusBadge status={article.status} /> · Odobravanje je blokirano dok sve tvrdnje nemaju odluku, ako postoji netačna/obmanjujuća tvrdnja, ili ako rubrika traži službeni izvor a nijedan nije naveden.</p>
      <div className="space-y-4" data-testid="claims-list">
        {claims.map((c) => (
          <ClaimForm key={c.id + c.updated_at} initial={c} idp={`claim-${c.id}`}
            onSave={(f) => call(() => api.put(`/admin/claims/${c.id}`, f), "Tvrdnja sačuvana")}
            onDelete={() => window.confirm("Obrisati tvrdnju?") && call(() => api.delete(`/admin/claims/${c.id}`), "Tvrdnja obrisana")} />
        ))}
        {!claims.length && <Empty testId="claims-empty">Još nema evidentiranih tvrdnji.</Empty>}
      </div>
      <h2 className="mb-3 mt-8 text-sm font-bold uppercase tracking-[0.12em] text-muted-foreground">Nova tvrdnja</h2>
      <ClaimForm idp="new-claim" onSave={(f) => call(() => api.post(`/admin/articles/${id}/claims`, f), "Tvrdnja dodana")} />
    </div>
  );
}

export function FactCheckQueue() {
  const { data, loading } = useFetch("/admin/factcheck/queue");
  if (loading) return <BrandLoader />;
  return (
    <div data-testid="factcheck-queue-page">
      <PageTitle title="Provjera činjenica" sub="Članci u fazi provjere i tvrdnje kojima je istekao rok ponovne provjere." />
      <Panel title={`Na provjeri · ${data.queue.length}`} className="mb-6" testId="fc-queue">
        {data.queue.length ? <ul className="divide-y divide-border text-sm">{data.queue.map((a) => (
          <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
            <Link to={`/admin/provjera/${a.id}`} className="font-medium hover:underline" data-testid={`fc-item-${a.id}`}>{a.headline}</Link>
            <span className="text-xs text-muted-foreground">Tvrdnje: {a.claims_total} · otvoreno: {a.claims_open}{a.deadline && ` · rok ${fmtDateTime(a.deadline)}`}</span>
          </li>))}</ul> : <Empty>Nema članaka na provjeri.</Empty>}
      </Panel>
      <Panel title="Ponovna provjera dospjela" testId="fc-recheck">
        {data.recheck_due.length ? <ul className="divide-y divide-border text-sm">{data.recheck_due.map((c) => (
          <li key={c.id} className="py-2"><Link to={`/admin/provjera/${c.article_id}`} className="font-medium hover:underline">{c.headline}</Link><p className="text-xs text-muted-foreground">„{c.text}" · {DECISIONS[c.decision]} · rok {fmtDate(c.recheck_at)}</p></li>))}</ul> : <Empty>Nema dospjelih provjera.</Empty>}
      </Panel>
    </div>
  );
}
